import React, { cloneElement, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
// import { useHistory } from 'react-router'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'

import Form from '../../../../components/Form'
import StepFormSubmit from './StepFormSubmit'
import SkeletonForm from '../../../../components/Form/Skeleton'
import FormProgress from '../../Form/FormProgress'

import { ReactComponent as IconBack } from '../../../../assets/icons/chevron-arrow-down.svg'

import { useAppendFormScrollbarToDrawer } from './hooks'
import useStepForm from '../../../../hooks/formHooks/useStepForm'
import useSuccessFormSubmit from '../../../../components/Form/hooks'

import { keys } from '../../../../helpers/common'
import { getStepWithError } from '../../../../components/Form/helpers'

import { activeStepSelector } from '../../../../modules/selectors/forms'

import useStyles from './style'
import useCommonStyles from '../../../../styles/common/stepForms'

const StepForm = React.memo(
  ({
    steps,
    formik,
    formName,
    successSubmit,
    clearSubmitHandler,
    formProgressItems,
    // automatically place the submit button on last step
    submitOnLastStep = true,
    isInitialDataLoading,
    isLoadingSelector,
    isSubmitDataLoading,
    errorSelector,
    submitText,
    onSuccessSubmit,
    ...props
  }) => {
    const { t } = useTranslation()
    const activeStep = useSelector(activeStepSelector)
    const stepsEl = useRef(null)
    const formContainerRef = useRef(null)

    const { resetForm, touched, setTouched, errors } = formik
    // for useStepForm purified stepFormik is used to avoid unnecessary functions changes on original formik change
    const stepFormik = useMemo(() => ({ touched, setTouched, errors }), [touched, setTouched, errors])

    // steps container spacing to represent active step
    // added here instead of the classes because classes sometimes not update with JSS
    const shownStepSpacing = useMemo(() => activeStep * -100, [activeStep])

    useAppendFormScrollbarToDrawer(formContainerRef.current)
    const { changeStep, backStep, resetSteps, handleStepChange, isStepFieldsValid } = useStepForm({
      // todo saved form data functionality is disabled till post MVP
      // initialStep: savedLineItemFacebookForm.step,
      stepFormik,
      stepCarouselEl: stepsEl,
      formName
    })

    const classes = useStyles({ activeStep, isFormProgressExist: !!formProgressItems })
    const commonClasses = useCommonStyles()

    const visibleSteps = useMemo(() => steps.filter(step => step.show), [steps])

    const getComponentProps = (component, index) => {
      // populate component with props during the rendering
      return cloneElement(component, {
        key: index,
        isActive: activeStep === index,
        formik,
        handleStepChange,
        isStepFieldsValid
      })
    }

    const handleStepFormErrorSubmit = useCallback(
      error => {
        // handle StepForm errors - focus error field Step in the view
        const errorFields = keys(error.errors)
        const scrollToStep = getStepWithError(error.errors, errorFields)

        if (scrollToStep !== null && scrollToStep >= 0) {
          // push user to found step if it's found
          changeStep(scrollToStep)
        }
      },
      [changeStep]
    )

    const handleStepFormSuccessSubmit = useCallback(() => {
      resetSteps()
      onSuccessSubmit && onSuccessSubmit()
    }, [resetSteps, onSuccessSubmit])

    const handleSuccessSubmit = useSuccessFormSubmit({
      onSuccessSubmit: handleStepFormSuccessSubmit,
      formName,
      resetForm
    })

    // todo update after research this topic. This version has some bugs,
    //  like working only on create campaign form, history.goBack() only in Chrome
    // const onBackButtonEvent = useCallback(() => {
    //   if (activeStep !== 0) {
    //     window.history.pushState(null, '', null)
    //     changeStep(activeStep - 1)
    //   } else {
    //     // if activeStep is 0 - redirect to previous URL
    //     history.goBack()
    //   }
    // }, [activeStep, changeStep, history])
    //
    // useEffect(() => {
    //   // prevent back button and move user to previous question on clicking it
    //   window.history.pushState(null, '', null)
    //   window.addEventListener('popstate', onBackButtonEvent)
    //
    //   return () => window.removeEventListener('popstate', onBackButtonEvent)
    // }, [onBackButtonEvent])

    return (
      <>
        <button type="button" className={classes.backBtn} onClick={backStep}>
          <IconBack /> {t('Back')}
        </button>
        {formProgressItems && <FormProgress items={formProgressItems} />}

        {isInitialDataLoading ? (
          <SkeletonForm className={classes.form} />
        ) : (
          <div className={classes.stepFormContainer} ref={formContainerRef}>
            <Form
              formName={formName}
              formik={formik}
              className={classes.form}
              onErrorSubmit={handleStepFormErrorSubmit}
              successSubmit={successSubmit}
              showSubmit={false}
              isLoadingSelector={isLoadingSelector}
              errorSelector={errorSelector}
              clearHandler={clearSubmitHandler}
              {...props}
            >
              <div className={classes.steps} ref={stepsEl} style={{ transform: `translateX(${shownStepSpacing}%)` }}>
                {visibleSteps.map(({ component, requireMinHeight }, index) => {
                  const isLastStep = visibleSteps.length - 1 === index
                  return (
                    // data-step attribute is required here!
                    // it's used for tracking field's step at the BE error stage
                    <div
                      key={index}
                      className={classnames(classes.stepItem, {
                        requireMinHeight
                      })}
                      data-step={index}
                    >
                      {getComponentProps(component, index)}
                      {submitOnLastStep && isLastStep && (
                        <div className={commonClasses.stepFooter}>
                          {/* it's recommended to keep the submit button within the form logic. Also it allows to keep steps
                     component logic simple and avoid selector usage duplications */}
                          <StepFormSubmit
                            successSubmit={successSubmit}
                            isLoadingSelector={isLoadingSelector}
                            errorSelector={errorSelector}
                            clearHandler={clearSubmitHandler}
                            onSuccessSubmit={handleSuccessSubmit}
                            submitText={submitText}
                            isSubmitDataLoading={isSubmitDataLoading}
                            // disable submit button when last step is not active,
                            // so form can't be submitted from the previous steps clicking Enter
                            disabled={activeStep !== index}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </Form>
          </div>
        )}
      </>
    )
  }
)

StepForm.propTypes = {
  steps: PropTypes.array,
  formName: PropTypes.string.isRequired,
  formik: PropTypes.object,
  submitText: PropTypes.string,
  formProgressItems: PropTypes.array,
  isInitialDataLoading: PropTypes.bool,
  isSubmitDataLoading: PropTypes.bool,
  successSubmit: PropTypes.bool,
  submitOnLastStep: PropTypes.bool
}

export default StepForm
